<template>
  <div class="column content-box">
    <div class="report-header">
      <h5 class="page-title">{{ $t('RESOLVED_CONVERSATIONS_REPORTS.HEADER') }}</h5>
      <p class="page-sub-title">{{ $t('RESOLVED_CONVERSATIONS_REPORTS.DESCRIPTION') }}</p>
    </div>
    <div class="filter-container">
      <div class="date-filter">
        <label>{{ $t('RESOLVED_CONVERSATIONS_REPORTS.DATE_START') }}</label>
        <input
          v-model="dateStart"
          type="date"
          class="date-input"
        />
      </div>
      <div class="date-filter">
        <label>{{ $t('RESOLVED_CONVERSATIONS_REPORTS.DATE_END') }}</label>
        <input
          v-model="dateEnd"
          type="date"
          class="date-input"
        />
      </div>
      <div class="filter-actions">
        <woot-button
          color-scheme="success"
          class-names="bg-primary"
          icon="arrow-download"
          :is-loading="isProcessing"
          :is-disabled="!dateStart || !dateEnd || isProcessing"
          @click="generateReport"
        >
          {{ isProcessing ? $t('RESOLVED_CONVERSATIONS_REPORTS.PROCESSING') : $t('RESOLVED_CONVERSATIONS_REPORTS.GENERATE') }}
        </woot-button>
      </div>
    </div>

    <div v-if="isProcessing" class="processing-status">
      <woot-loading-state :message="$t('RESOLVED_CONVERSATIONS_REPORTS.PROCESSING_MESSAGE')" />
    </div>

    <div v-if="reportError" class="report-error">
      <p class="error-message">{{ reportError }}</p>
    </div>

    <div v-if="reportReady" class="report-ready">
      <div class="report-success">
        <fluent-icon icon="checkmark-circle" size="24" />
        <span>{{ $t('RESOLVED_CONVERSATIONS_REPORTS.READY_MESSAGE') }}</span>
        <woot-button
          color-scheme="success"
          class-names="bg-primary"
          icon="arrow-download"
          @click="downloadReport"
        >
          {{ $t('RESOLVED_CONVERSATIONS_REPORTS.DOWNLOAD') }}
        </woot-button>
      </div>
    </div>
  </div>
</template>

<script>
import alertMixin from 'shared/mixins/alertMixin';
import ReportsAPI from '../../../../api/reports';

export default {
  name: 'ResolvedConversationsReports',
  mixins: [alertMixin],
  data() {
    return {
      dateStart: '',
      dateEnd: '',
      isProcessing: false,
      reportReady: false,
      reportError: '',
      currentToken: null,
      pollingTimer: null,
    };
  },
  beforeDestroy() {
    this.stopPolling();
  },
  methods: {
    async generateReport() {
      this.reportReady = false;
      this.reportError = '';
      this.isProcessing = true;

      const since = Math.floor(new Date(this.dateStart + 'T00:00:00').getTime() / 1000);
      const until = Math.floor(new Date(this.dateEnd + 'T23:59:59').getTime() / 1000);

      try {
        const response = await ReportsAPI.generateResolvedConversationsReport({ since, until });
        this.currentToken = response.data.token;
        this.startPolling();
      } catch (error) {
        this.isProcessing = false;
        this.reportError = this.$t('RESOLVED_CONVERSATIONS_REPORTS.GENERATE_ERROR');
      }
    },
    startPolling() {
      this.pollingTimer = setInterval(async () => {
        try {
          const response = await ReportsAPI.checkResolvedReportStatus(this.currentToken);
          if (!response.data.processing) {
            this.stopPolling();
            this.isProcessing = false;
            if (response.data.error) {
              this.reportError = response.data.error;
            } else {
              this.reportReady = true;
            }
          }
        } catch (error) {
          this.stopPolling();
          this.isProcessing = false;
          this.reportError = this.$t('RESOLVED_CONVERSATIONS_REPORTS.STATUS_ERROR');
        }
      }, 3000);
    },
    stopPolling() {
      if (this.pollingTimer) {
        clearInterval(this.pollingTimer);
        this.pollingTimer = null;
      }
    },
    async downloadReport() {
      try {
        const response = await ReportsAPI.downloadResolvedReport(this.currentToken);
        const blob = new Blob([response.data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `relatorio_conversas_resolvidas_${this.dateStart}_${this.dateEnd}.csv`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        this.showAlert(this.$t('RESOLVED_CONVERSATIONS_REPORTS.DOWNLOAD_ERROR'));
      }
    },
  },
};
</script>

<style scoped lang="scss">
.report-header {
  margin-bottom: var(--space-normal);

  .page-title {
    font-size: var(--font-size-large);
    font-weight: var(--font-weight-bold);
    margin-bottom: var(--space-smaller);
  }

  .page-sub-title {
    font-size: var(--font-size-small);
    color: var(--color-body);
  }
}

.filter-container {
  display: flex;
  align-items: flex-end;
  gap: var(--space-normal);
  margin-bottom: var(--space-large);
  flex-wrap: wrap;
}

.date-filter {
  display: flex;
  flex-direction: column;

  label {
    font-size: var(--font-size-small);
    font-weight: var(--font-weight-medium);
    margin-bottom: var(--space-micro);
  }

  .date-input {
    padding: var(--space-small);
    border: 1px solid var(--color-border);
    border-radius: var(--border-radius-normal);
    font-size: var(--font-size-small);
    height: 40px;
    margin: 0;
  }
}

.filter-actions {
  display: flex;
  align-items: flex-end;
}

.processing-status {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--space-large);
}

.report-error {
  .error-message {
    color: var(--r-500);
    padding: var(--space-normal);
    background: var(--r-50);
    border-radius: var(--border-radius-normal);
  }
}

.report-ready {
  .report-success {
    display: flex;
    align-items: center;
    gap: var(--space-small);
    padding: var(--space-normal);
    background: var(--g-50);
    border-radius: var(--border-radius-normal);
    color: var(--g-700);
  }
}
</style>
